import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { z } from 'zod';

import { AuthLayout } from '@cold-ambulance/layout';
import {
  Button,
  CardContent,
  Checkbox,
  Input,
  Label,
} from '@cold-ambulance/shadcn';
import {
  EmailInput,
  FieldMessage,
  FormMessage,
  PasswordInput,
  client,
  errorToFormIssue,
  signUpEmail,
} from '@cold-ambulance/ui';

import { auth } from '../../firebase.tsx';
import { type Step, StepCircles } from './stepper.tsx';

const schema = z
  .object({
    name: z.string().min(1, { message: 'اسم مقدم الخدمة مطلوب' }),
    email: z
      .string()
      .min(1, { message: 'البريد الإلكتروني مطلوب' })
      .email({ message: 'البريد الإلكتروني غير صحيح' }),
    password: z
      .string()
      .min(8, { message: 'كلمة المرور يجب أن تكون 8 أحرف على الأقل' }),
    confirmPassword: z.string().min(1, { message: 'يرجى تأكيد كلمة المرور' }),
    termsAccepted: z.boolean().refine((accepted) => accepted, {
      message: 'يجب الموافقة على الشروط والأحكام',
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'كلمتا المرور غير متطابقتين',
    path: ['confirmPassword'],
  });

export default function SignupAccount({
  onSubmitSuccess,
  step,
  index,
}: {
  onSubmitSuccess: () => void;
  step: Step;
  index: number;
}) {
  const { t } = useTranslation();
  const form = useForm({
    defaultValues: {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      termsAccepted: false,
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async (props) => {
        try {
          const { email, password, name } = props.value;
          const { subject } = await signUpEmail(auth, email, password, name);
          await client.request('POST /vendor', {
            name: name,
            id: subject.uid,
            email: email,
            termsAccepted: true,
          });
          onSubmitSuccess?.();
          return void 0;
        } catch (error) {
          auth.currentUser?.delete().catch(() => {});

          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <AuthLayout
      stepIndex={index}
      title="سجل معنا"
      className="border"
      subtitle={
        <div className="flex gap-x-1">
          <p>لديك حساب؟</p>
          <Link to={'/signin'} className="text-primary">
            تسجيل الدخول
          </Link>
        </div>
      }
    >
      <StepCircles step={step} index={index} />

      <CardContent className="px-0">
        <form
          className="mt-6 mb-4 space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit();
          }}
        >
          <form.Field
            name="name"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="name">
                  {t('profile.serviceProviderName')}
                </Label>
                <Input
                  id="name"
                  type="text"
                  placeholder={t('placeholders.companyName')}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="email"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="email">
                  {t('common.email')}
                </Label>
                <EmailInput
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="password"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="password">
                  {t('common.password')}
                </Label>
                <PasswordInput
                  id="password"
                  placeholder="・・・・・・"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="confirmPassword"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="confirmPassword">
                  {t('signup.confirmPassword')}
                </Label>
                <PasswordInput
                  id="confirmPassword"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="termsAccepted"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="termsAccepted"
                    checked={field.state.value}
                    onCheckedChange={(checked) =>
                      field.handleChange(checked === true)
                    }
                  />
                  <span className="text-sm">
                    <label htmlFor="termsAccepted" className="cursor-pointer">
                      {t('signup.termsAcceptPrefix')}
                    </label>{' '}
                    <a
                      href="https://medhub.tgl.com.sa/terms-and-privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      {t('signup.privacyPolicy')}
                    </a>{' '}
                    {t('signup.termsAnd')}{' '}
                    <a
                      href="https://medhub.tgl.com.sa/vendor-agreement"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      {t('signup.vendorAgreement')}
                    </a>
                  </span>
                </div>
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <Button
                type="submit"
                disabled={!canSubmit}
                className="mt-8 w-full cursor-pointer"
              >
                {isSubmitting ? t('signup.creatingAccount') : t('common.next')}
              </Button>
            )}
          />
          <form.Subscribe
            selector={(state) => state.errors}
            children={(errors) => <FormMessage errors={errors} />}
          />
        </form>
      </CardContent>
    </AuthLayout>
  );
}
